import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../../components/ui/sheet";
import {
  BellRing,
  Search,
  CalendarDays,
  ExternalLink,
  Inbox,
  ChevronRight,
  Sparkles,
  CircleAlert,
  HistoryIcon,
} from "lucide-react";
import NotificationService from "./NotificationService";
import NotificationMessageService from "./NotificationMessageService";

// ---------------------------------------------------------------------------
// HTML parser
// Format stocké en DB :
//   <p><a href="https://app.peppacorp.fr/aide/550"><strong>Nom</strong></a> : description</p>
//   <hr />
//   <p>...</p>
// ---------------------------------------------------------------------------
function parseEmailHTML(raw) {
  if (!raw) return [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(raw, "text/html");
  const paragraphs = Array.from(doc.querySelectorAll("p"));

  return paragraphs.map((p) => {
    const anchor = p.querySelector("a");
    if (!anchor) return { name: null, url: null, aidId: null, description: p.textContent.trim() };

    const url = anchor.getAttribute("href") ?? "";
    const name = anchor.textContent.trim();
    const idMatch = url.match(/\/aide\/(\d+)/);
    const fullText = p.textContent.trim();
    const afterLink = fullText.slice(name.length).replace(/^\s*:\s*/, "").trim();

    return { name, url, aidId: idMatch ? idMatch[1] : null, description: afterLink };
  }).filter((a) => a.name || a.description);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(dateStr) {
  if (!dateStr) return "Date inconnue";
  return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function formatDateShort(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

// ---------------------------------------------------------------------------
// AidItem
// ---------------------------------------------------------------------------
function AidItem({ aid }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/60 transition-colors border border-transparent hover:border-border/40">
      <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-2" />
      <div className="flex-1 min-w-0">
        {aid.name ? (
          <a
            href={aid.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-semibold text-sm text-primary hover:underline mb-1"
            onClick={(e) => e.stopPropagation()}
          >
            {aid.name}
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        ) : (
          <span className="text-xs text-muted-foreground italic">Aide sans titre</span>
        )}
        {aid.description && (
          <p className="text-sm text-muted-foreground leading-relaxed">{aid.description}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReportCard
// ---------------------------------------------------------------------------
function ReportCard({ message, notificationTitle, isLatest, onClick }) {
  const aids = useMemo(() => parseEmailHTML(message.message), [message.message]);
  const aidCount = aids.filter((a) => a.name).length;

  return (
    <Card
      className={`group relative cursor-pointer border transition-all duration-200 overflow-hidden ${
        isLatest
          ? "highlighted border-primary/40 hover:border-primary/60 hover:shadow-lg"
          : "border-border/40 hover:border-primary/30 hover:shadow-md bg-card/80 backdrop-blur-sm"
      }`}
      onClick={onClick}
    >
      <div className={`absolute inset-y-0 left-0 w-0.5 rounded-l-full transition-all duration-200 ${
        isLatest ? "bg-primary/60" : "bg-primary/0 group-hover:bg-primary/60"
      }`} />
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {formatDateShort(message.timestamp)}
              </span>
              {isLatest && (
                <Badge className="text-[10px] px-1.5 py-0 h-4 bg-primary/15 text-primary border-primary/30 font-semibold">
                  Dernier rapport
                </Badge>
              )}
            </div>
            <h3 className="font-bold text-foreground text-base mb-1 truncate">
              {notificationTitle ?? "Veille"}
            </h3>
            {aidCount > 0 ? (
              <p className="text-sm text-muted-foreground line-clamp-1">
                <span className="text-foreground font-medium">
                  {aidCount} aide{aidCount > 1 ? "s" : ""} identifiée{aidCount > 1 ? "s" : ""}
                </span>
                {aids[0]?.name && ` · ${aids[0].name}`}
                {aidCount > 1 && ` +${aidCount - 1}`}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">Aucune aide identifiée</p>
            )}
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary/60 transition-colors shrink-0 mt-1" />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ReportDetail (inside Sheet)
// ---------------------------------------------------------------------------
function ReportDetail({ message, notificationTitle }) {
  const aids = useMemo(() => parseEmailHTML(message.message), [message.message]);
  const hasAids = aids.some((a) => a.name);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 items-center">
        <Badge variant="secondary" className="text-xs gap-1">
          <CalendarDays className="w-3 h-3" />
          {formatDate(message.timestamp)}
        </Badge>
        <Badge variant="outline" className="text-xs gap-1">
          <BellRing className="w-3 h-3" />
          {notificationTitle ?? "Veille"}
        </Badge>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          Aides identifiées
        </p>
        {hasAids ? (
          <div className="space-y-2">
            {aids.filter((a) => a.name).map((aid, i) => (
              <AidItem key={i} aid={aid} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 rounded-lg p-4">
            <CircleAlert className="w-4 h-4 shrink-0" />
            Aucune aide correspondante identifiée pour cette période.
          </div>
        )}
      </div>

      {/* <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Email complet
        </p>
        <div
          className="text-sm text-foreground/80 bg-muted/30 rounded-lg p-4 border border-border/30 leading-relaxed prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: message.message ?? "<em>Contenu indisponible</em>" }}
        />
      </div> */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AlertsHistoryPage() {
  const [notifications, setNotifications] = useState([]);
  const [messagesMap, setMessagesMap] = useState({});
  const [loadingNotifs, setLoadingNotifs] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    setLoadingNotifs(true);
    try {
      const res = await NotificationService.getNotification();
      setNotifications(res.data ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingNotifs(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  useEffect(() => {
    if (notifications.length === 0) return;
    setLoadingMessages(true);
    Promise.all(
      notifications.map((n) =>
        NotificationMessageService.getMessagesForNotification(n.id)
          .then((res) => ({ id: n.id, data: res.data ?? [] }))
          .catch(() => ({ id: n.id, data: [] }))
      )
    ).then((results) => {
      const map = {};
      results.forEach(({ id, data }) => { map[id] = data; });
      setMessagesMap(map);
      setLoadingMessages(false);
    });
  }, [notifications]);

  const allReports = useMemo(() => {
    const list = [];
    notifications.forEach((notif) => {
      (messagesMap[notif.id] ?? []).forEach((msg) => {
        list.push({ message: msg, notification: notif });
      });
    });
    return list.sort((a, b) => new Date(b.message.timestamp) - new Date(a.message.timestamp));
  }, [notifications, messagesMap]);

  const latestMessageId = allReports[0]?.message?.id ?? null;

  const filteredReports = useMemo(() => {
    let result = allReports;
    if (selectedFilter !== "all") {
      result = result.filter((r) => r.notification.id === selectedFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((r) =>
        r.notification.title?.toLowerCase().includes(q) ||
        r.message.message?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [allReports, selectedFilter, search]);

  const loading = loadingNotifs || loadingMessages;
  const totalReports = allReports.length;

  return (
    <div className="min-h-screen app-page-gradient">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        <div>
          <div className="flex items-center gap-2 mb-2 pt-10 md:pt-0">
            <HistoryIcon className="hidden sm:block w-5 h-5 text-primary" />
            <h1 className="text-4xl font-bold text-foreground">
              Historique des <span className="text-primary">rapports</span>
            </h1>
          </div>
          <p className="text-muted-foreground">
            Consultez l'ensemble des alertes hebdomadaires générées pour vos veilles actives.
          </p>
        </div>

        <div className="space-y-3">
          {!loadingNotifs && notifications.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                Filtrer par veille
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedFilter("all")}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                    selectedFilter === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card/60 text-foreground border-border/40 hover:border-primary/40 hover:bg-accent/30"
                  }`}
                >
                  Toutes les veilles
                  <span className="ml-1.5 text-xs opacity-70">({totalReports})</span>
                </button>
                {notifications.map((n) => {
                  const count = (messagesMap[n.id] ?? []).length;
                  return (
                    <button
                      key={n.id}
                      onClick={() => setSelectedFilter(n.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                        selectedFilter === n.id
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card/60 text-foreground border-border/40 hover:border-primary/40 hover:bg-accent/30"
                      }`}
                    >
                      {n.title ?? `Veille #${n.id}`}
                      <span className="ml-1.5 text-xs opacity-70">({count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher dans les rapports..."
              className="pl-9 bg-card/60 border-border/40"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 rounded-xl bg-muted/50 animate-pulse" />
            ))}
          </div>
        ) : filteredReports.length === 0 ? (
          <Card className="border-border/30 border-dashed bg-card/40">
            <CardContent className="py-16 flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Inbox className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground mb-1">
                  {totalReports === 0 ? "Aucun rapport reçu" : "Aucun résultat"}
                </h2>
                <p className="text-sm text-muted-foreground max-w-sm">
                  {totalReports === 0
                    ? "Vos rapports hebdomadaires apparaîtront ici dès que votre première veille aura été traitée."
                    : "Essayez de modifier vos filtres ou votre recherche."}
                </p>
              </div>
              {totalReports === 0 && (
                <Button onClick={() => navigate(RouterPath.ALERTS)} variant="outline" className="mt-2">
                  Voir mes veilles actives
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredReports.map(({ message, notification }) => (
              <ReportCard
                key={`${notification.id}-${message.id}`}
                message={message}
                notificationTitle={notification.title}
                isLatest={message.id === latestMessageId}
                onClick={() => {
                  setSelectedReport({ message, notificationTitle: notification.title });
                  setSheetOpen(true);
                }}
              />
            ))}
          </div>
        )}
      </div>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto text-foreground">
          {selectedReport && (
            <>
              <SheetHeader className="mb-6">
                <div className="flex items-center gap-2">
                  <BellRing className="w-5 h-5 text-primary shrink-0" />
                  <SheetTitle className="text-lg leading-tight">
                    {selectedReport.notificationTitle ?? "Veille"}
                  </SheetTitle>
                </div>
                {/* <SheetDescription>
                  {formatDate(selectedReport.message.timestamp)}
                </SheetDescription> */}
              </SheetHeader>
              <ReportDetail
                message={selectedReport.message}
                notificationTitle={selectedReport.notificationTitle}
              />
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}